// ReportChartBarsExtras.jsx

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
    Box,
    Container,
    Stack,
    Table,
    TableBody,
    Typography,
} from "@mui/material";

import EChartsReact from "echarts-for-react";

import HoverPaper from "./shared/HoverPaper";
import NoDataRow from "./NoDataRow";
import FiltersMetricSelection from "./FiltersMetricSelection";
import { COMPARISON_FILTER, DEFAULT_COMPARISON } from "../helpers/filters";
import { fns } from "../helpers/common";
import Colours from "../helpers/colours";
import InfoIcon from "./InfoIcon";

const ReportChartBarsExtras = ({
    data,
    title,
    description,
    emptyMessage = "No data for these filters",
    filters = [],
    entriesLimit = 5,
    entriesLimitSort = "descending",
    initialMetric = "totalCost",
    metrics = {
        totalCost: {
            label: "Total cost",
            short: "Total revenue",
            format: { left: "currency" },
        },
        count: {
            label: "Count",
            short: "Number sold",
            format: {},
        },
    },
}) => {
    const js = useSelector((state) => state.globalReportFilters);
    const { id: hotelID } = useSelector((state) => state.hotelID);

    const [metric, setMetric] = useState(initialMetric);
    const [formattedData, setFormattedData] = useState({});
    const [total, setTotal] = useState(0);

    const [update, setUpdate] = useState(0);

    useEffect(() => {
        if (!data || !data[""] || !data[""].result) {
            return;
        }

        const currentData = data[""].result.extrasByType ?? {};
        const lastYearData = data[""].resultOneYearBefore?.extrasByType ?? {};

        const currentKeys = Object.keys(currentData);
        const lastYearKeys = Object.keys(lastYearData);
        const unionKeys = Array.from(
            new Set([...currentKeys, ...lastYearKeys]),
        );

        // const filteredKeys = unionKeys.filter((extraName) => {
        //     const valueNow = currentData[extraName]?.[metric] ?? 0;
        //     return valueNow !== 0;
        // });

        const filteredKeys = unionKeys.filter((extraName) => {
            if (
                filters.length > 0 &&
                !filters.includes(extraName) &&
                js?.other?.excludeOtherExtrasIfFiltered
            ) {
                return false;
            }

            const valueNow = currentData[extraName]?.[metric] ?? 0;
            return valueNow !== 0;
        });

        let sums = {};

        // Armar la lista de los extras con su valor actual
        filteredKeys.forEach((extraName) => {
            const valueNow = currentData[extraName]?.[metric] ?? 0;
            sums[extraName] = valueNow;
        });

        let sorted = Object.entries(sums).sort((a, b) =>
            entriesLimitSort === "ascending" ? a[1] - b[1] : b[1] - a[1],
        );

        let top = sorted.slice(0, entriesLimit);
        let leftover = sorted.slice(entriesLimit);

        let finalKeys = top.map(([k]) => k);
        let othersSumThisYear = 0;
        let othersSumLastYear = 0;

        let aux_total = 0;

        const seriesDataThisYear = [];
        const seriesDataLastYear = [];

        top.forEach(([extraName]) => {
            const valueNow = currentData[extraName]?.[metric] ?? 0;
            const valueLast = lastYearData[extraName]?.[metric] ?? 0;
            seriesDataThisYear.push(valueNow);
            seriesDataLastYear.push(valueLast);
            aux_total += valueNow;
        });

        if (leftover.length > 0) {
            leftover.forEach(([extraName]) => {
                othersSumThisYear += currentData[extraName]?.[metric] ?? 0;
                othersSumLastYear += lastYearData[extraName]?.[metric] ?? 0;
            });

            finalKeys.push("Others");
            seriesDataThisYear.push(othersSumThisYear);
            seriesDataLastYear.push(othersSumLastYear);
            aux_total += othersSumThisYear;
        }

        let comparisonLabel =
            COMPARISON_FILTER[js?.filters?.Comparison ?? DEFAULT_COMPARISON]
                ?.label || "Previous year";

        const series = [
            {
                name: "Current Period",
                type: "bar",
                data: seriesDataThisYear,
                itemStyle: { color: "black" },
                lineStyle: { color: "black" },
                symbolStyle: { color: "black" },
                label: {
                    show: true,
                    position: "top",
                    formatter: (params) => {
                        return fns(hotelID, params.value, {});
                    },
                    fontSize: 10,
                    color: "black",
                },
            },
            {
                name: comparisonLabel,
                type: "bar",
                data: seriesDataLastYear,
                itemStyle: { color: "grey" },
                lineStyle: { color: "grey" },
                symbolStyle: { color: "grey" },
                label: {
                    show: true,
                    position: "top",
                    formatter: (params) => {
                        return fns(hotelID, params.value, {});
                    },
                    fontSize: 10,
                    color: "black",
                },
            },
        ];

        setFormattedData({
            xAxisData: finalKeys,
            series,
        });
        setTotal(aux_total);
        setUpdate((prev) => prev + 1);
    }, [data, metric, entriesLimit, entriesLimitSort, js, hotelID]);

    useEffect(() => {
        setMetric(initialMetric);
    }, [metrics, initialMetric]);

    return (
        <Stack>
            <InfoIcon title={title} description={description} />
            <Container
                maxWidth={false}
                component={HoverPaper}
                sx={{ p: 2, backgroundColor: Colours.notificationCard }}
            >
                <Box>
                    {Object.keys(metrics).length > 1 ? (
                        <Stack direction="row" alignItems="baseline">
                            <FiltersMetricSelection
                                metric={metric}
                                metrics={metrics}
                                setMetric={setMetric}
                            />
                            <Typography>by extras</Typography>
                        </Stack>
                    ) : (
                        ""
                    )}
                </Box>

                {!data || !data[""] || !data[""].result ? (
                    <Table>
                        <TableBody>
                            <NoDataRow text={emptyMessage} />
                        </TableBody>
                    </Table>
                ) : (
                    <>
                        <br />
                        {/* Total */}
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography>
                                Total:{" "}
                                <span style={{ fontWeight: "bold" }}>
                                    {fns(
                                        hotelID,
                                        total,
                                        metrics[metric]?.format ?? {},
                                    )}
                                </span>
                            </Typography>
                        </Stack>

                        {/* Chart */}
                        {Object.values(data[""].result.extrasByType ?? {})
                            .length > 0 && (
                            <EChartsReact
                                key={update}
                                option={{
                                    tooltip: {
                                        trigger: "axis",
                                        formatter: function (params) {
                                            let tooltipHtml = `<div style="border-collapse: collapse;">`;
                                            tooltipHtml += `<div style="margin-bottom: 5px;"><strong>${params[0].name}</strong></div>`;

                                            params.forEach(function (item) {
                                                tooltipHtml +=
                                                    '<div style="display: flex; justify-content: space-between; padding: 0px;">';
                                                tooltipHtml += `<div style="padding-left:3px;">${item.marker}</div>`;
                                                tooltipHtml += `<div style="flex: 1; text-align: left; padding-left:4px;">${item.seriesName}</div>`;
                                                tooltipHtml += `<div style="padding-left:15px;"><strong>${fns(
                                                    hotelID,
                                                    item.value,
                                                    {},
                                                )}</strong></div>`;
                                                tooltipHtml += "</div>";
                                            });

                                            tooltipHtml += "</div>";
                                            return tooltipHtml;
                                        },
                                    },
                                    legend: { show: false },
                                    xAxis: {
                                        type: "category",
                                        data: formattedData.xAxisData,
                                        axisLabel: {
                                            interval: 0,
                                            textStyle: {
                                                fontWeight: "light",
                                            },
                                            formatter: function (value) {
                                                if (value?.length > 15) {
                                                    return (
                                                        value.slice(0, 15) +
                                                        "..."
                                                    );
                                                }
                                                return value;
                                            },
                                        },
                                    },
                                    yAxis: {
                                        name: metrics[metric]?.short ?? "",
                                        nameLocation: "middle",
                                        nameGap: 50,
                                        nameTextStyle: {
                                            fontSize: 14,
                                        },
                                        axisLabel: {
                                            formatter: function (value) {
                                                if (value >= 1000) {
                                                    return `${value / 1000}k`;
                                                }
                                                return value;
                                            },
                                        },
                                    },
                                    series: formattedData.series,
                                }}
                            />
                        )}
                    </>
                )}
            </Container>
        </Stack>
    );
};

export default ReportChartBarsExtras;
